import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { apiFetch } from "../utils/api";
import { abrirArchivoSeguro } from "../utils/upload";
import {
  Badge,
  BadgeFormaPago,
  estadoVisible,
  dineroMoneda,
  sumarPorMoneda,
  textoPorMoneda,
  mapaEstadosSolicitud,
} from "../utils/estados";
import ConfirmarAprobacionModal from "../components/ConfirmarAprobacionModal";
import AvisoModal from "../components/AvisoModal";
import PromptModal from "../components/PromptModal";
import { TEMA, thTema, tituloSeccionTema } from "../components/TemaDetalle";
import TablaProductosModal from "../components/TablaProductosModal";
import { Ayuda } from "../context/AyudaContext";
import Cargando from "../components/Cargando";

// Pantalla de UNA solicitud pendiente de aprobación: todo el detalle de lo que
// pidió el solicitante, lo que subió compras y los botones para aprobar o
// rechazar. La lista de pendientes (SolicitudesPendientes) solo resume.
function DetalleAprobacion() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [solicitudes, setSolicitudes] = useState([]);
  // Ventana "¿Estás seguro de aprobar…?" — guarda qué se va a aprobar y la
  // acción a ejecutar si el gerente confirma.
  const [confirmacion, setConfirmacion] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [procesando, setProcesando] = useState(null);
  // Reemplaza los alert() nativos por un modal propio, más acorde al resto
  // de la app.
  const [aviso, setAviso] = useState(null);
  // Reemplaza el window.prompt() del motivo de rechazo — guarda la etiqueta
  // a mostrar y qué hacer con el texto que escriba el gerente.
  const [rechazoPendiente, setRechazoPendiente] = useState(null);
  // Después de aprobar solo una parte, hay que decidir qué pasa con el
  // resto: { solicitudId, items: [{id, descripcion}] }.
  const [decisionResto, setDecisionResto] = useState(null);

  const cargar = async () => {
    setCargando(true);
    setError("");
    try {
      const detalle = await apiFetch(`/api/solicitudes/${id}`);
      setSolicitudes([detalle]);
      return detalle;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Aprueba solo los ítems que el gerente dejó marcados, cotización por
  // cotización — puede aprobar unos productos de una cotización y dejar
  // otros sin aprobar a propósito, o aprobar solo una cantidad parcial de
  // alguno (compras/el sobrante se resuelven aparte). seleccionPorGrupo es
  // { [cotizacionId]: { itemIds: [...], cantidades: {itemId: cantidadParcial} } }.
  // Devuelve los productos que quedaron con cantidad sobrante (aprobación
  // parcial) para que quien llama decida si hay que abrir la ventana de
  // "qué hacemos con el resto".
  const aprobarSeleccion = async (solicitudId, seleccionPorGrupo) => {
    setProcesando(solicitudId);
    const restantes = [];
    const aprobados = [];
    let mensajeParcial = null;
    try {
      for (const [cotizacionId, seleccion] of Object.entries(seleccionPorGrupo)) {
        const itemIds = seleccion?.itemIds;
        if (!itemIds || itemIds.length === 0) continue;
        const cantidades = seleccion.cantidades && Object.keys(seleccion.cantidades).length > 0 ? seleccion.cantidades : undefined;
        const resultado = await apiFetch(`/api/solicitudes/${solicitudId}/aprobar`, {
          method: "POST",
          body: JSON.stringify({ item_ids: itemIds, cotizacion_id: Number(cotizacionId), cantidades }),
        });
        if (resultado.parcialesRestantes?.length > 0) {
          restantes.push(...resultado.parcialesRestantes);
        }
        if (resultado.aprobados?.length > 0) aprobados.push(...resultado.aprobados);
        if (!resultado.completa) mensajeParcial = resultado.message;
      }
      cargar();
    } catch (err) {
      setAviso({ tipo: "error", titulo: "No se pudo aprobar", mensaje: err.message });
      return { error: true, restantes: [], aprobados: [], mensajeParcial: null };
    } finally {
      setProcesando(null);
    }
    return { error: false, restantes, aprobados, mensajeParcial };
  };

  // Resumen final de una aprobación: lo aprobado y, si hubo, lo que el gerente
  // no aprobó con su desenlace (compra cancelada, vuelve a cotizarse o sigue
  // sin decidir). Se muestra recién cuando ya no queda nada por decidir.
  // noAprobados: [{ descripcion, cantidad, unidad }]
  const mostrarResumenAprobacion = ({ aprobados, noAprobados, desenlace, mensajeParcial }) => {
    const hayDetalle = (aprobados?.length || 0) > 0 || (noAprobados?.length || 0) > 0;
    if (!hayDetalle && !mensajeParcial) return;
    setAviso({
      tipo: "info",
      titulo: "Aprobación registrada",
      mensaje: hayDetalle ? "Se registró tu aprobación. Este es el resumen:" : mensajeParcial,
      aprobados: aprobados || [],
      noAprobados: noAprobados || [],
      desenlace,
    });
  };

  // Rechaza SOLO los ítems de esta cotización (no toda la solicitud).
  // definitivo=false: vuelve a cotizando para que compras traiga una
  // cotización distinta. definitivo=true: el gerente decidió que esos
  // productos no se compran — quedan cerrados, no vuelven a aparecer.
  // resumen (opcional): { aprobados, productos } — viene cuando el rechazo es la
  // decisión sobre el resto de una aprobación, para cerrar con un solo resumen.
  const rechazarCotizacion = async (solicitudId, itemIds, comentario, definitivo, resumen) => {
    setProcesando(solicitudId);
    try {
      for (const itemId of itemIds) {
        await apiFetch(`/api/solicitudes/${solicitudId}/items/${itemId}/rechazar`, {
          method: "POST",
          body: JSON.stringify({ comentario, definitivo: !!definitivo }),
        });
      }
      const detalle = await cargar();
      // Solo cuenta lo que ESTE aprobador puede decidir: lo que queda fuera de su rango lo aprueba otra persona.
      const quedaAlgo = detalle && detalle.estado === "en_cola" && meCorresponde(detalle);
      if (!resumen && !quedaAlgo) {
        navigate("/pendientes");
        return;
      }
      if (resumen) {
        mostrarResumenAprobacion({
          aprobados: resumen.aprobados,
          noAprobados: resumen.productos,
          desenlace: definitivo ? "cancelado" : "recotizar",
        });
      }
    } catch (err) {
      setAviso({ tipo: "error", titulo: "No se pudo rechazar", mensaje: err.message });
    } finally {
      setProcesando(null);
      setRechazoPendiente(null);
    }
  };

  const rechazar = async (id, comentario) => {
    setProcesando(id);
    try {
      await apiFetch(`/api/solicitudes/${id}/rechazar`, {
        method: "POST",
        body: JSON.stringify({ comentario }),
      });
      // La solicitud ya no está pendiente: se vuelve a la lista en vez de quedarse en esta pantalla.
      navigate("/pendientes");
      return;
    } catch (err) {
      setAviso({ tipo: "error", titulo: "No se pudo rechazar", mensaje: err.message });
    } finally {
      setProcesando(null);
      setRechazoPendiente(null);
    }
  };

  // Abre el modal para escribir el motivo — al aceptar, ejecuta el rechazo
  // correspondiente (de una cotización puntual o de toda la solicitud).
  // productos: [{ descripcion, cantidad, unidad }] — se muestran en una tabla.
  const pedirMotivoRechazoCotizacion = (solicitudId, itemIds, productos, definitivo, resumen) => {
    setRechazoPendiente({
      solicitudId,
      definitivo: !!definitivo,
      productos,
      etiqueta: definitivo
        ? "¿Por qué no se van a comprar estos productos? Quedan cerrados y no vuelven a cotizarse."
        : "Estos productos vuelven a cotización para que compras traiga otro proveedor. ¿Por qué se rechaza esta cotización?",
      textoBoton: definitivo ? "Rechazar definitivamente" : "Rechazar",
      ejecutar: (comentario) =>
        rechazarCotizacion(solicitudId, itemIds, comentario, definitivo, resumen && { ...resumen, productos }),
    });
  };
  const pedirMotivoRechazoSolicitud = (id) => {
    // Si ya se aprobó algo, avisar que rechazar todo lo cancela (y anula sus
    // pagos pendientes): antes la ventana solo pedía el motivo.
    const s = solicitudes.find((x) => x.id === id);
    const yaAprobados = (s?.items || []).filter((it) => Number(it.cantidad_aprobada) > 0);
    setRechazoPendiente({
      solicitudId: id,
      etiqueta:
        yaAprobados.length > 0
          ? `⚠️ Ya aprobaste: ${yaAprobados
              .map((it) => it.descripcion_producto)
              .join(", ")}. Al rechazar la solicitud completa se cancela esa aprobación y se anulan sus pagos pendientes. Motivo del rechazo:`
          : "Motivo del rechazo:",
      ejecutar: (comentario) => rechazar(id, comentario),
    });
  };

  // Cada monto va en la moneda de su cotización; si hay de las dos, se muestra uno por moneda.
  const totalRecomendado = (s) =>
    textoPorMoneda(
      sumarPorMoneda(
        s.cotizaciones
          .filter((c) => c.seleccionada && lineasPendientesDe(s, c).length > 0)
          .map((c) => ({ moneda: c.moneda, monto: montoPendienteDe(s, c) }))
      )
    );

  // Un producto solo aparece en la lista mientras le quede algo por decidir al aprobador
  // (lo ya aprobado o rechazado definitivamente no se vuelve a mostrar como pendiente).
  // Ids de los productos que siguen pendientes de decidir en la solicitud.
  const idsPendientesDe = (sol) => new Set(sol.items.filter((it) => quedaPorDecidir(it)).map((it) => it.id));
  // Líneas de una cotización que todavía hay que decidir: productos pendientes que ESTA cotización no aprobó ya
  // (si Comtech ya aprobó sus 2 monitores, su cotización no se vuelve a ofrecer por el monitor que falta).
  const lineasPendientesDe = (sol, c) => {
    const pendientes = idsPendientesDe(sol);
    return c.items.filter((ci) => pendientes.has(ci.id) && !ci.aprobada);
  };
  // Monto de una cotización limitado a los productos pendientes (con IVA, salvo proveedor exonerado).
  // Si la cotización no trae productos ya aprobados, es su monto total tal cual.
  const montoPendienteDe = (sol, c) => {
    const lineas = lineasPendientesDe(sol, c);
    if (lineas.length === c.items.length) return Number(c.monto_total);
    const subtotal = lineas.reduce((suma, ci) => suma + Number(ci.precio || 0), 0);
    return c.exento_iva ? subtotal : Math.round(subtotal * (1 + Number(c.iva_porcentaje ?? 15) / 100) * 100) / 100;
  };

  // Y solo los que compras ya eligió con cotización (los que siguen cotizando no se le muestran).
  const quedaPorDecidir = (it) =>
    it.estado_item === "validado" &&
    !it.descartado &&
    Number(it.cantidad) - Number(it.cantidad_aprobada || 0) - Number(it.cantidad_descartada || 0) > 0;

  const folioDe = (s) => `#${String(s.id).padStart(3, "0")}`;
  // El precio de cada producto vive en la cotización puntual (c.items), no
  // en el ítem de la solicitud — el mismo producto puede tener un precio
  // distinto según qué proveedor lo esté cotizando.
  const itemsComoLista = (s, c, ids) =>
    s.items
      .filter((it) => ids.includes(it.id))
      .map((it) => ({
        id: it.id,
        descripcion: it.descripcion_producto,
        // Lo que queda por decidir (si ya se aprobó una parte, no se vuelve a ofrecer la cantidad completa),
        // sin pasar de lo que esa cotización cubre: si el proveedor cotizó 2 de 3, solo se pueden aprobar 2 con él.
        cantidad: Math.min(
          Number(it.cantidad) - Number(it.cantidad_aprobada || 0) - Number(it.cantidad_descartada || 0),
          Number(c.items.find((ci) => ci.id === it.id)?.cantidad) > 0
            ? Number(c.items.find((ci) => ci.id === it.id).cantidad)
            : Infinity
        ),
        unidad: it.unidad,
        precio: c.items.find((ci) => ci.id === it.id)?.precio ?? null,
        // Cantidad que cubre el precio de la cotización (puede ser distinta a la pedida).
        cantidadBase: c.items.find((ci) => ci.id === it.id)?.cantidad ?? it.cantidad,
      }));

  // Aprobar UNA cotización (la fila tocada): se muestra qué productos se
  // aprueban y, si no es la recomendada por compras, cuál era esa.
  const pedirConfirmacionFila = (s, c, idsPendientes) => {
    const recomendada = c.seleccionada
      ? null
      : s.cotizaciones.find((x) => x.seleccionada && x.items.some((it) => idsPendientes.includes(it.id) && !it.aprobada)) || null;
    setConfirmacion({
      solicitudId: s.id,
      folio: folioDe(s),
      grupos: [{ id: c.id, cotizacion: c, items: itemsComoLista(s, c, idsPendientes), recomendada }],
      nota: null,
      ejecutar: (seleccionPorGrupo) => aprobarSeleccion(s.id, seleccionPorGrupo),
    });
  };

  // "Aprobar todo": el detalle de TODO lo que se va a aprobar, cotización por
  // cotización (las que caen fuera de su rango de montos no se incluyen).
  const pedirConfirmacionTodo = (s) => {
    const pendientes = s.items.filter((it) => it.estado_item === "validado" && !it.aprobado && quedaPorDecidir(it)).map((it) => it.id);
    const pendientesDe = (c) => c.items.filter((ci) => !ci.aprobada).map((it) => it.id).filter((id) => pendientes.includes(id));
    const grupos = s.cotizaciones
      .filter((c) => c.seleccionada && c.puede_aprobar !== false && pendientesDe(c).length > 0)
      .map((c) => ({ id: c.id, cotizacion: c, items: itemsComoLista(s, c, pendientesDe(c)), recomendada: null }));
    if (grupos.length === 0) return;
    const hayFueraDeRango = s.cotizaciones.some(
      (c) => c.seleccionada && c.puede_aprobar === false && pendientesDe(c).length > 0
    );
    setConfirmacion({
      solicitudId: s.id,
      folio: folioDe(s),
      grupos,
      nota: hayFueraDeRango
        ? "Las cotizaciones que caen fuera de tu rango de aprobación no se incluyen: las aprueba otra persona."
        : null,
      ejecutar: (seleccionPorGrupo) => aprobarSeleccion(s.id, seleccionPorGrupo),
    });
  };

  const confirmarAprobacion = async (seleccionPorGrupo, excluidosInfo) => {
    const actual = confirmacion;
    const { error, restantes, aprobados, mensajeParcial } = await actual.ejecutar(seleccionPorGrupo);
    setConfirmacion(null);
    if (error) return;
    // Si dejó productos sin aprobar (los desmarcó a propósito) o aprobó
    // menos cantidad de la que traía la cotización, hay que decidir qué
    // pasa con lo que quedó sin resolver antes de seguir.
    const pendientesPorResolver = [
      ...(excluidosInfo || []),
      ...restantes.map((r) => ({ id: r.id, descripcion: r.descripcion, cantidad: r.cantidad })),
    ];
    if (pendientesPorResolver.length > 0) {
      // El resumen se muestra después de que el gerente decida qué hacer.
      setDecisionResto({ solicitudId: actual.solicitudId, items: pendientesPorResolver, aprobados });
    } else {
      mostrarResumenAprobacion({ aprobados, mensajeParcial });
    }
  };

  // El gerente cierra la ventana sin decidir: el resumen deja claro que esos
  // productos siguen sin resolver.
  const decidirMasTarde = () => {
    const actual = decisionResto;
    setDecisionResto(null);
    if (actual) {
      mostrarResumenAprobacion({ aprobados: actual.aprobados, noAprobados: actual.items, desenlace: "pendiente" });
    }
  };

  if (cargando) return <Cargando />;

  // Un aprobador con rango de montos solo ve lo que le corresponde por el
  // monto de la cotización elegida; lo demás lo aprueba otra persona.
  const meCorresponde = (s) => {
    const pendientes = s.items
      .filter((it) => it.estado_item === "validado" && !it.aprobado && quedaPorDecidir(it))
      .map((it) => it.id);
    // Cuenta cualquier cotización con algo pendiente que esa cotización no haya aprobado ya (no solo la recomendada).
    return s.cotizaciones.some(
      (c) => c.puede_aprobar !== false && c.items.some((it) => pendientes.includes(it.id) && !it.aprobada)
    );
  };
  const solicitudesVisibles = solicitudes.filter(meCorresponde);
  // Solo cuentan las que todavía tienen algo por aprobar y que le tocan a otra persona; una solicitud ya
  // resuelta no está "oculta por monto".
  const ocultasPorMonto = solicitudes.filter((s) => !meCorresponde(s) && s.items.some((it) => it.estado_item === "validado" && quedaPorDecidir(it))).length;
  const rangoAprobacion = solicitudes.find((s) => s.rango_aprobacion)?.rango_aprobacion;

  const esUrgente = (s) => (s.items || []).some((it) => it.prioridad === "urgente");

  return (
    <div>
      <Link to="/pendientes" style={styles.linkVolver}>
        ← Regresar a solicitudes pendientes
      </Link>
      <h2 style={{ margin: "0.6rem 0 0", color: "var(--tema-titulo, #0b3c60)" }}>Detalle de la solicitud #{String(id).padStart(3, "0")}</h2>
      <Ayuda>
        <p style={{ color: "#5b6472", marginTop: "0.25rem", fontSize: "0.9rem" }}>
          Revisa lo que pidió el solicitante y las cotizaciones de compras, y aprueba o rechaza.
        </p>
      </Ayuda>

      {error && <p style={{ color: "#a3402f", marginTop: "1rem" }}>{error}</p>}

      {(rangoAprobacion || ocultasPorMonto > 0) && (
        <div style={styles.avisoRango}>
          {rangoAprobacion && (
            <>
              💼 Tu rango de aprobación: <strong>{rangoAprobacion}</strong>.{" "}
            </>
          )}
          {ocultasPorMonto > 0 &&
            `Hay ${ocultasPorMonto} solicitud(es) pendiente(s) cuyo monto le corresponde aprobar a otra persona.`}
        </div>
      )}

      {!error && solicitudesVisibles.length === 0 && (
        <div className="vacio-card" style={styles.vacioCard}>
          <div style={styles.vacioIconoWrap}>
            <div style={styles.vacioGlow} />
            <span className="vacio-anillo" style={styles.vacioAnillo} />
            <div style={styles.vacioIcono}>✅</div>
          </div>
          <h3 style={styles.vacioTitulo}>Esta solicitud ya no está pendiente de tu aprobación</h3>
          <p style={styles.vacioTexto}>
            Ya se resolvió, o todavía no tiene una cotización lista, o su monto le corresponde aprobarlo a otra
            persona.
          </p>
          <Link to="/pendientes" className="vacio-refrescar" style={{ ...styles.vacioBoton, textDecoration: "none", display: "inline-block" }}>
            ← Regresar a solicitudes pendientes
          </Link>
        </div>
      )}

      {solicitudesVisibles.map((s) => {
        // Solo lo que de verdad queda por decidir (sin lo ya aprobado ni lo rechazado definitivamente).
        const itemsPorAprobar = s.items.filter((it) => it.estado_item === "validado" && !it.aprobado && quedaPorDecidir(it));
        const itemsYaAprobados = s.items.filter((it) => it.estado_item === "validado" && it.aprobado);
        // Un producto rechazado definitivamente (o aprobado en parte y el resto rechazado) queda "cotizando"
        // por dentro, pero ya no lo está cotizando nadie: solo cuenta lo que todavía tiene cantidad por resolver.
        const itemsSinCotizar = s.items.filter(
          (it) =>
            it.estado_item !== "validado" &&
            !it.descartado &&
            Number(it.cantidad) - Number(it.cantidad_aprobada || 0) - Number(it.cantidad_descartada || 0) > 0
        );
        return (
        <div
          key={s.id}
          style={{
            ...styles.card,
            ...(esUrgente(s) ? { borderLeft: "4px solid #a3402f" } : {}),
          }}
        >
          <div style={styles.cardHeader}>
            <div style={{ display: "flex", gap: "0.75rem" }}>
              <div style={styles.iconoSolicitud}>📄</div>
              <div>
                <strong style={{ fontSize: "1.05rem", display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                  #{String(s.id).padStart(3, "0")} — {s.area_nombre}
                  {esUrgente(s) && <span style={styles.badgeUrgente}>🔴 Urgente</span>}
                </strong>
                <div style={styles.subtexto}>Solicitud de compra</div>
              </div>
            </div>
            <div style={styles.totalBox}>
              <div style={styles.totalLabel}>TOTAL RECOMENDADO</div>
              <div style={styles.totalValor}>{totalRecomendado(s)}</div>
            </div>
          </div>

          <div style={styles.datosCard}>
            <div style={styles.datosEncabezado}>
              <div className="form-seccion-titulo" style={{ margin: 0 }}>📋 Datos de la solicitud</div>
              <Badge
                estado={estadoVisible(
                  s.estado,
                  s.total_cotizaciones,
                  s.tiene_pendiente_aprobacion,
                  s.en_atencion,
                  s.tiene_pago_pendiente,
                  s.tiene_pago_realizado
                )}
                mapa={mapaEstadosSolicitud()}
              />
            </div>
            <div style={styles.datosPanel}>
              <div>
                <span style={styles.etiquetaDato}>👤 Solicitante</span>
                {s.solicitante_nombre}
              </div>
              <div>
                <span style={styles.etiquetaDato}>🏢 Área</span>
                {s.area_nombre}
              </div>
              <div>
                <span style={styles.etiquetaDato}>🏗️ Edificio</span>
                {s.edificios?.length > 0 ? s.edificios.map((ed) => ed.nombre).join(", ") : s.edificio_nombre}
              </div>
              <div>
                <span style={styles.etiquetaDato}>📅 Fecha de solicitud</span>
                {new Date(s.creado_en).toLocaleDateString("es-NI")}
              </div>
              <div style={styles.datosRequerimiento}>
                <span style={styles.etiquetaDato}>📝 Requerimiento</span>
                {s.descripcion_requerimiento}
              </div>
            </div>
          </div>

          {itemsYaAprobados.length > 0 && (
            <p style={{ fontSize: "0.8rem", color: "#2f7a54", margin: "0 0 0.5rem" }}>
              ✓ Ya aprobaste antes: {itemsYaAprobados.map((it) => it.descripcion_producto).join(", ")}
            </p>
          )}

          {itemsSinCotizar.length > 0 && (
            <p style={{ fontSize: "0.8rem", color: "#5b6472", margin: "0 0 0.6rem" }}>
              ℹ️ Compras todavía está cotizando {itemsSinCotizar.length} producto(s) más de esta solicitud. Te los enviará
              a aprobación cuando tenga las cotizaciones que necesita.
            </p>
          )}

          {(() => {
            // Cuadro comparativo: un producto por fila y un proveedor por columna, para ver
            // los precios lado a lado. Abajo, el total, la entrega, la forma de pago y los botones.
            const productos = s.items.filter(quedaPorDecidir);
            const cots = s.cotizaciones.filter((c) => lineasPendientesDe(s, c).length > 0);
            const lineaDe = (c, itemId) => lineasPendientesDe(s, c).find((ci) => ci.id === itemId);
            const simbolo = (c) => (c.moneda === "USD" ? "US$" : "C$");
            const dinero = (c, n) =>
              `${simbolo(c)}${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
            const unitario = (ci) => (Number(ci.cantidad) > 0 ? Number(ci.precio) / Number(ci.cantidad) : null);
            // Para comparar dólares con córdobas se usa el tipo de cambio de cada cotización.
            const enCordobas = (c, n) =>
              n == null ? null : c.moneda === "USD" ? (Number(c.tipo_cambio) > 0 ? n * Number(c.tipo_cambio) : null) : n;
            const masBaratoDe = (itemId) => {
              const precios = cots
                .map((c) => {
                  const ci = lineaDe(c, itemId);
                  return ci ? { id: c.id, valor: enCordobas(c, unitario(ci)) } : null;
                })
                .filter((x) => x && x.valor != null);
              if (precios.length < 2) return null;
              const minimo = Math.min(...precios.map((x) => x.valor));
              return new Set(precios.filter((x) => Math.abs(x.valor - minimo) < 0.005).map((x) => x.id));
            };
            const recomendadas = cots.filter((c) => c.seleccionada);
            const productosMasBaratos = (c) =>
              productos.filter((it) => masBaratoDe(it.id)?.has(c.id)).length;

            const accionesDe = (c) => {
              const idsCotizacion = lineasPendientesDe(s, c).map((it) => it.id);
              const idsPendientes = idsCotizacion.filter((pid) => itemsPorAprobar.some((p) => p.id === pid));
              const yaAprobada = !!c.seleccionada && idsCotizacion.some((pid) => itemsYaAprobados.some((p) => p.id === pid));
              if (idsPendientes.length > 0 && c.puede_aprobar === false) {
                return <span style={{ fontSize: "0.75rem", color: "#b8722a", fontWeight: 600 }}>Fuera de tu rango</span>;
              }
              if (idsPendientes.length > 0) {
                return (
                  <div style={{ display: "flex", gap: "0.35rem", justifyContent: "center", flexWrap: "wrap" }}>
                    <button
                      type="button"
                      disabled={procesando === s.id}
                      onClick={() => pedirConfirmacionFila(s, c, idsPendientes)}
                      style={styles.botonAprobarFila}
                    >
                      ✓ Aprobar
                    </button>
                    <button
                      type="button"
                      disabled={procesando === s.id}
                      onClick={() =>
                        pedirMotivoRechazoCotizacion(
                          s.id,
                          idsPendientes,
                          s.items
                            .filter((it) => idsPendientes.includes(it.id))
                            .map((it) => ({
                              descripcion: it.descripcion_producto,
                              cantidad: it.cantidad - (it.cantidad_aprobada || 0) - (it.cantidad_descartada || 0),
                              unidad: it.unidad,
                            }))
                        )
                      }
                      style={styles.botonRechazarFila}
                    >
                      ✕ Rechazar
                    </button>
                  </div>
                );
              }
              return yaAprobada ? (
                <span style={{ fontSize: "0.75rem", color: "#2f7a54", fontWeight: 600 }}>✓ Aprobada</span>
              ) : (
                "—"
              );
            };

            return (
              <>
                {recomendadas.length > 0 && (
                  <div style={styles.cajaRecomendacion}>
                    <span style={{ fontSize: "1.4rem" }}>★</span>
                    <div>
                      <div style={{ fontWeight: 800, color: "#1f6b45" }}>
                        Compras recomienda:{" "}
                        {recomendadas.map((c) => `${c.proveedor_nombre} (${dineroMoneda(montoPendienteDe(s, c), c.moneda)})`).join(" + ")}
                      </div>
                      {recomendadas.map((c) => {
                        const cubre = lineasPendientesDe(s, c).length;
                        const baratos = productosMasBaratos(c);
                        return (
                          <div key={c.id} style={{ fontSize: "0.8rem", color: "#3d6b55", marginTop: "0.15rem" }}>
                            {c.proveedor_nombre}: cubre {cubre} de {productos.length} producto{productos.length === 1 ? "" : "s"}
                            {cots.length > 1 &&
                              (baratos === 0 ? (
                                <span style={styles.avisoNoMasBarato}>⚠ No tiene el precio más bajo en ningún producto</span>
                              ) : (
                                ` · precio más bajo en ${baratos} de ${cubre}`
                              ))}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <p style={styles.tituloTabla}>
                  Comparación de precios por producto ({productos.length} producto{productos.length === 1 ? "" : "s"} ·{" "}
                  {cots.length} cotizaci{cots.length === 1 ? "ón" : "ones"})
                </p>
                <div className="comparador-escritorio" style={styles.comparadorScroll}>
                  <table className="sin-tarjetas" style={styles.comparador}>
                    <thead>
                      <tr>
                        <th style={{ ...styles.thComparador, ...styles.colFijaComparador, zIndex: 3, textAlign: "left", minWidth: 230 }}>
                          Producto
                        </th>
                        <th style={{ ...styles.thComparador, textAlign: "center" }}>Cantidad</th>
                        {cots.map((c) => (
                          <th
                            key={c.id}
                            style={{ ...styles.thComparador, ...(c.seleccionada ? styles.thRecomendada : {}), minWidth: 170 }}
                          >
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.4rem" }}>
                              <span style={{ ...styles.avatar, ...(c.seleccionada ? styles.avatarVerde : styles.avatarGris) }}>
                                {c.proveedor_nombre.charAt(0).toUpperCase()}
                              </span>
                              {c.proveedor_nombre}
                            </div>
                            {!!c.seleccionada && <div style={styles.etiquetaRecomendada}>★ Recomendada por compras</div>}
                            {c.archivo_cotizacion && (
                              <a
                                href="#ver-cotizacion"
                                onClick={(e) => abrirArchivoSeguro(e, c.archivo_cotizacion)}
                                style={styles.linkCotizacion}
                              >
                                📄 Ver cotización
                              </a>
                            )}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {productos.map((it, fila) => {
                        const detalleDistinto =
                          it.descripcion_detallada &&
                          it.descripcion_detallada.trim().toLowerCase() !== it.descripcion_producto.trim().toLowerCase();
                        const aprobadaParcial = it.cantidad_aprobada > 0 && it.cantidad_aprobada < it.cantidad;
                        const baratos = masBaratoDe(it.id);
                        const fondoFila = fila % 2 === 1 ? "#f7f9fc" : "#fff";
                        return (
                          <tr key={it.id}>
                            <td style={{ ...styles.tdComparador, ...styles.colFijaComparador, background: fondoFila, boxShadow: "inset -1px 0 0 #e2e6ea" }}>
                              <div style={{ fontWeight: 600, color: "var(--tema-titulo, #0b3c60)" }}>
                                <span style={{ color: "#8993a1", fontWeight: 500 }}>{it.numero_item}.</span> {it.descripcion_producto}
                                {it.prioridad === "urgente" && <span style={{ ...styles.badgeUrgente, marginLeft: "0.4rem" }}>🔴 Urgente</span>}
                              </div>
                              {detalleDistinto && <div style={styles.detalleProducto}>{it.descripcion_detallada}</div>}
                            </td>
                            <td style={{ ...styles.tdComparador, background: fondoFila, textAlign: "center", whiteSpace: "nowrap" }}>
                              {Number(it.cantidad) - Number(it.cantidad_aprobada || 0) - Number(it.cantidad_descartada || 0)}
                              {it.unidad ? ` ${it.unidad}` : ""}
                              {aprobadaParcial && (
                                <div style={styles.detalleProducto}>
                                  Ya aprobaste {it.cantidad_aprobada} de {it.cantidad}
                                </div>
                              )}
                            </td>
                            {cots.map((c) => {
                              const ci = lineaDe(c, it.id);
                              const esBarato = baratos?.has(c.id);
                              const fondo = c.seleccionada ? (fila % 2 === 1 ? "#e3f3ea" : "#ecf8f1") : fondoFila;
                              if (!ci) {
                                return (
                                  <td key={c.id} style={{ ...styles.tdComparador, background: fondo, textAlign: "center", color: "#a0a8b3", fontSize: "0.78rem" }}>
                                    No cotizó
                                  </td>
                                );
                              }
                              const pu = unitario(ci);
                              return (
                                <td key={c.id} style={{ ...styles.tdComparador, background: fondo, textAlign: "center" }}>
                                  <div style={{ fontWeight: 700, fontVariantNumeric: "tabular-nums", color: esBarato ? "#1f7a4a" : "#1f2a3a" }}>
                                    {pu != null ? `${dinero(c, pu)} c/u` : dinero(c, ci.precio)}
                                  </div>
                                  <div style={{ fontSize: "0.72rem", color: "#5b6472", fontVariantNumeric: "tabular-nums" }}>
                                    Total {dinero(c, ci.precio)}
                                    {Number(ci.cantidad) !== Number(it.cantidad) - Number(it.cantidad_aprobada || 0) - Number(it.cantidad_descartada || 0) && ` · cotizó ${ci.cantidad}`}
                                  </div>
                                  {esBarato && <span style={styles.etiquetaMasBajo}>▼ Más bajo</span>}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={2} style={{ ...styles.tdPie, ...styles.colFijaComparador, textAlign: "right" }}>
                          Total (con IVA)
                        </td>
                        {cots.map((c) => (
                          <td key={c.id} style={{ ...styles.tdPie, ...(c.seleccionada ? styles.tdPieRecomendada : {}), fontSize: "0.95rem" }}>
                            {dineroMoneda(montoPendienteDe(s, c), c.moneda)}
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <td colSpan={2} style={{ ...styles.tdPieSecundario, ...styles.colFijaComparador, textAlign: "right" }}>
                          Entrega · Forma de pago
                        </td>
                        {cots.map((c) => (
                          <td key={c.id} style={{ ...styles.tdPieSecundario, ...(c.seleccionada ? styles.tdPieRecomendada : {}) }}>
                            <div style={{ fontSize: "0.76rem", marginBottom: "0.2rem" }}>{c.tiempo_entrega || "—"}</div>
                            <BadgeFormaPago valor={c.forma_pago} />
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <td colSpan={2} style={{ ...styles.tdPieSecundario, ...styles.colFijaComparador, textAlign: "right" }}>
                          Decisión
                        </td>
                        {cots.map((c) => (
                          <td key={c.id} style={{ ...styles.tdPieSecundario, ...(c.seleccionada ? styles.tdPieRecomendada : {}) }}>
                            {accionesDe(c)}
                          </td>
                        ))}
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Celular: el cuadro no cabe a lo ancho; cada proveedor es una tarjeta con sus
                    productos, y "Más bajo" marca dónde tiene el mejor precio. */}
                <div className="comparador-celular">
                  {cots.map((c) => (
                    <div key={c.id} style={{ ...styles.tarjetaProveedor, ...(c.seleccionada ? styles.tarjetaProveedorRecomendada : {}) }}>
                      <div style={styles.tarjetaProveedorEncabezado}>
                        <span style={{ ...styles.avatar, ...(c.seleccionada ? styles.avatarVerde : styles.avatarGris) }}>
                          {c.proveedor_nombre.charAt(0).toUpperCase()}
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 800, color: "var(--tema-titulo, #0b3c60)" }}>{c.proveedor_nombre}</div>
                          {!!c.seleccionada && <span style={styles.badgeRecomendada}>★ Recomendada por compras</span>}
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontSize: "0.66rem", fontWeight: 700, color: "#8993a1", textTransform: "uppercase" }}>Total con IVA</div>
                          <div style={{ fontWeight: 800, fontSize: "1.05rem", color: "var(--tema-titulo, #0b3c60)" }}>
                            {dineroMoneda(montoPendienteDe(s, c), c.moneda)}
                          </div>
                        </div>
                      </div>
                      <div style={styles.tarjetaProveedorDatos}>
                        <span>🚚 {c.tiempo_entrega || "Entrega sin indicar"}</span>
                        <BadgeFormaPago valor={c.forma_pago} />
                        {c.archivo_cotizacion && (
                          <a href="#ver-cotizacion" onClick={(e) => abrirArchivoSeguro(e, c.archivo_cotizacion)} style={{ fontWeight: 600 }}>
                            📄 Ver cotización
                          </a>
                        )}
                      </div>
                      {productos.map((it) => {
                        const ci = lineaDe(c, it.id);
                        const esBarato = masBaratoDe(it.id)?.has(c.id);
                        const pu = ci ? unitario(ci) : null;
                        return (
                          <div key={it.id} style={styles.tarjetaProveedorLinea}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 600, fontSize: "0.84rem" }}>
                                {it.numero_item}. {it.descripcion_producto}
                              </div>
                              {ci && (
                                <div style={{ fontSize: "0.72rem", color: "#5b6472" }}>
                                  {ci.cantidad} × {pu != null ? dinero(c, pu) : "—"}
                                </div>
                              )}
                            </div>
                            <div style={{ textAlign: "right", flexShrink: 0 }}>
                              {ci ? (
                                <>
                                  <div style={{ fontWeight: 700, fontVariantNumeric: "tabular-nums", color: esBarato ? "#1f7a4a" : "#1f2a3a" }}>
                                    {dinero(c, ci.precio)}
                                  </div>
                                  {esBarato && <span style={styles.etiquetaMasBajo}>▼ Más bajo</span>}
                                </>
                              ) : (
                                <span style={{ fontSize: "0.76rem", color: "#a0a8b3" }}>No cotizó</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      <div style={{ marginTop: "0.6rem" }}>{accionesDe(c)}</div>
                    </div>
                  ))}
                </div>

                <Ayuda>
                  <p style={{ fontSize: "0.78rem", color: "#5b6472", margin: "0.4rem 0 0" }}>
                    Los precios por unidad no llevan IVA; el total sí. "Más bajo" compara en córdobas con el tipo de cambio
                    de cada cotización.
                  </p>
                </Ayuda>
              </>
            );
          })()}

          <div style={{ display: "flex", gap: "0.6rem", alignItems: "center", marginTop: "1rem", flexWrap: "wrap" }}>
            {s.items.some(quedaPorDecidir) && (
            <button
              disabled={procesando === s.id}
              onClick={() => pedirConfirmacionTodo(s)}
              style={{ ...styles.boton, ...styles.botonAprobar }}
            >
              ✓ Aprobar todo
            </button>
            )}
            {/* Con rango limitado, rechazar TODA la solicitud solo si todo lo pendiente cae dentro del rango propio. */}
            {s.items.some(quedaPorDecidir) &&
              (s.cotizaciones.some((c) => c.seleccionada && c.puede_aprobar === false && lineasPendientesDe(s, c).length > 0) ? (
                <span style={{ fontSize: "0.8rem", color: "#9c7a1a" }}>
                  Hay cotizaciones fuera de tu rango: no puedes rechazar toda la solicitud, solo lo que está dentro de tu rango.
                </span>
              ) : (
                <button
                  disabled={procesando === s.id}
                  onClick={() => pedirMotivoRechazoSolicitud(s.id)}
                  style={{ ...styles.boton, ...styles.botonRechazar }}
                >
                  ✕ Rechazar solicitud completa
                </button>
              ))}
          </div>
        </div>
        );
      })}

      {confirmacion && (
        <ConfirmarAprobacionModal
          grupos={confirmacion.grupos}
          folio={confirmacion.folio}
          nota={confirmacion.nota}
          procesando={procesando === confirmacion.solicitudId}
          onCancelar={() => setConfirmacion(null)}
          onConfirmar={confirmarAprobacion}
        />
      )}

      <AvisoModal
        tipo={aviso?.tipo}
        titulo={aviso?.titulo}
        mensaje={aviso?.mensaje}
        contenido={
          aviso?.aprobados?.length > 0 || aviso?.noAprobados?.length > 0 ? (
            <>
              {aviso.aprobados?.length > 0 && (
                <>
                  <div style={styles.tituloTablaModal}>✓ Aprobado ahora</div>
                  <TablaProductosModal
                    filas={aviso.aprobados.map((a) => ({
                      descripcion: a.descripcion,
                      cantidad: a.aprobada,
                      detalle: a.total,
                    }))}
                    encabezadoCantidad="Aprobada"
                    encabezadoExtra="Pedida"
                    tono="verde"
                  />
                </>
              )}
              {aviso.noAprobados?.length > 0 && (
                <>
                  <div style={styles.tituloTablaModal}>
                    {aviso.desenlace === "cancelado"
                      ? "✕ No aprobado por gerencia"
                      : aviso.desenlace === "recotizar"
                      ? "🔁 Vuelve a cotización"
                      : "⏳ Pendiente de decidir"}
                  </div>
                  <TablaProductosModal
                    filas={aviso.noAprobados.map((p) => ({
                      descripcion: p.descripcion,
                      cantidad: p.cantidad,
                      unidad: p.unidad,
                    }))}
                    encabezadoCantidad={aviso.desenlace === "cancelado" ? "No se compra" : "Sin aprobar"}
                    tono={aviso.desenlace === "cancelado" ? "rojo" : "naranja"}
                  />
                </>
              )}
            </>
          ) : null
        }
        onCerrar={() => {
          const resumenDeAprobacion = aviso?.titulo === "Aprobación registrada";
          setAviso(null);
          // Terminó la aprobación y ya no queda nada pendiente por decidir: se vuelve a la lista.
          const sol = solicitudes[0];
          // (Si queda algo, pero fuera de su rango de aprobación, también se sale: ya no le toca a él.)
          const quedaAlgo = sol && sol.estado === "en_cola" && meCorresponde(sol);
          if (resumenDeAprobacion && !quedaAlgo) navigate("/pendientes");
        }}
      />

      <PromptModal
        titulo={rechazoPendiente?.definitivo ? "Rechazar definitivamente" : "Rechazar"}
        etiqueta={rechazoPendiente?.etiqueta}
        ancho={rechazoPendiente?.productos?.length > 0 ? 520 : undefined}
        contenido={
          rechazoPendiente?.productos?.length > 0 ? (
            <TablaProductosModal
              filas={rechazoPendiente.productos}
              encabezadoCantidad={rechazoPendiente.definitivo ? "No se compra" : "Cantidad"}
              tono="rojo"
            />
          ) : null
        }
        textoBoton={rechazoPendiente?.textoBoton || "Rechazar"}
        procesando={procesando === rechazoPendiente?.solicitudId}
        onCancelar={() => setRechazoPendiente(null)}
        onAceptar={(comentario) => rechazoPendiente.ejecutar(comentario)}
      />

      {decisionResto && (
        <div style={styles.overlayDecision} onClick={decidirMasTarde}>
          <div style={styles.modalDecision} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 0.4rem", fontSize: "1.05rem", color: "var(--tema-titulo, #0b3c60)" }}>
              ¿Qué hacemos con el resto?
            </h3>
            <p style={{ margin: "0 0 0.2rem", fontSize: "0.88rem", color: "#3d4a5c" }}>
              Estos productos no se aprobaron. ¿Compras busca otro proveedor para ellos, o quedan cerrados sin
              comprarse?
            </p>
            <TablaProductosModal
              filas={decisionResto.items.map((i) => ({
                descripcion: i.descripcion,
                cantidad: i.cantidad,
                unidad: i.unidad,
              }))}
              encabezadoCantidad="Sin aprobar"
              tono="naranja"
            />
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              <button
                type="button"
                style={styles.botonDecisionCotizar}
                onClick={() => {
                  const { items, solicitudId, aprobados } = decisionResto;
                  setDecisionResto(null);
                  pedirMotivoRechazoCotizacion(solicitudId, items.map((i) => i.id), items, false, { aprobados });
                }}
              >
                🔁 Cotizar de nuevo con otro proveedor
              </button>
              <button
                type="button"
                style={styles.botonDecisionRechazar}
                onClick={() => {
                  const { items, solicitudId, aprobados } = decisionResto;
                  setDecisionResto(null);
                  pedirMotivoRechazoCotizacion(solicitudId, items.map((i) => i.id), items, true, { aprobados });
                }}
              >
                🚫 Rechazar definitivamente — no se compran
              </button>
              <button type="button" style={styles.botonDecisionLuego} onClick={decidirMasTarde}>
                Decidir más tarde
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  overlayDecision: {
    position: "fixed",
    inset: 0,
    background: "rgba(11,60,96,0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2100,
    padding: "1rem",
  },
  modalDecision: {
    background: "#fff",
    borderRadius: 16,
    padding: "1.4rem 1.5rem 1.2rem",
    width: "100%",
    maxWidth: 520,
    boxShadow: "0 16px 48px rgba(11,60,96,0.35)",
  },
  tituloTablaModal: { margin: "0.9rem 0 0", fontSize: "0.78rem", fontWeight: 700, color: "var(--tema-titulo, #0b3c60)", textTransform: "uppercase", letterSpacing: "0.02em" },
  botonDecisionCotizar: {
    background: "var(--tema-titulo, #0b3c60)",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "0.65rem 1rem",
    fontSize: "0.88rem",
    fontWeight: 700,
    cursor: "pointer",
    textAlign: "left",
  },
  botonDecisionRechazar: {
    background: "#fff",
    color: "#a3402f",
    border: "1px solid #a3402f",
    borderRadius: 8,
    padding: "0.65rem 1rem",
    fontSize: "0.88rem",
    fontWeight: 700,
    cursor: "pointer",
    textAlign: "left",
  },
  botonDecisionLuego: {
    background: "transparent",
    color: "#5b6472",
    border: "none",
    padding: "0.4rem 0.2rem",
    fontSize: "0.82rem",
    cursor: "pointer",
    textAlign: "center",
  },
  avisoRango: {
    marginTop: "1rem",
    padding: "0.6rem 0.9rem",
    borderRadius: 8,
    background: "var(--tema-titulo, #0b3c60)",
    color: "#fff",
    fontSize: "0.85rem",
  },
  vacioCard: {
    marginTop: "1.5rem",
    border: "1px solid #e2e6ea",
    borderRadius: 14,
    padding: "3rem 1.5rem 2.5rem",
    background: "linear-gradient(180deg, #f4f9f6 0%, #fff 55%)",
    boxShadow: "0 1px 3px rgba(27,42,74,0.06)",
    textAlign: "center",
  },
  vacioIconoWrap: {
    position: "relative",
    width: 96,
    height: 96,
    margin: "0 auto 0.9rem",
  },
  vacioGlow: {
    position: "absolute",
    inset: -10,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(47,122,84,0.16), rgba(47,122,84,0) 70%)",
  },
  vacioAnillo: {
    position: "absolute",
    inset: 16,
    borderRadius: "50%",
    border: "2px solid #2f7a54",
  },
  vacioIcono: {
    position: "absolute",
    inset: 16,
    borderRadius: "50%",
    background: "#dcf0e6",
    color: "#2f7a54",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1.8rem",
  },
  vacioEyebrow: {
    margin: "0 0 0.25rem",
    color: "#2f7a54",
    fontSize: "0.82rem",
    fontWeight: 700,
  },
  vacioTitulo: {
    margin: "0 0 0.4rem",
    color: "var(--tema-titulo, #0b3c60)",
    fontSize: "1.25rem",
  },
  vacioTexto: {
    margin: "0 auto",
    maxWidth: 380,
    color: "#5b6472",
    fontSize: "0.88rem",
    lineHeight: 1.5,
  },
  vacioBoton: {
    marginTop: "1.25rem",
    background: "#fff",
    border: "1px solid #d3d9df",
    borderRadius: 8,
    padding: "0.5rem 1.1rem",
    fontSize: "0.82rem",
    fontWeight: 600,
    color: "var(--tema-titulo, #0b3c60)",
    cursor: "pointer",
  },
  grupoArea: {
    marginBottom: "1.75rem",
  },
  tituloArea: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    fontSize: "1rem",
    fontWeight: 700,
    color: "var(--tema-titulo, #0b3c60)",
    margin: "0 0 0.75rem",
    paddingBottom: "0.4rem",
    borderBottom: "2px solid var(--tema-suave, #dceaf5)",
  },
  contadorArea: {
    fontSize: "0.75rem",
    fontWeight: 700,
    color: "var(--tema-primario, #2f6690)",
    background: "var(--tema-suave, #dceaf5)",
    borderRadius: 999,
    padding: "0.1rem 0.55rem",
  },
  card: {
    border: "1px solid #e2e6ea",
    borderRadius: 14,
    padding: "1.25rem 1.4rem",
    marginBottom: "1.25rem",
    background: "#fff",
    boxShadow: "0 1px 3px rgba(27,42,74,0.06)",
  },
  badgeUrgente: {
    fontSize: "0.7rem",
    fontWeight: 700,
    color: "#a3402f",
    background: "#f7ded9",
    borderRadius: 999,
    padding: "0.1rem 0.55rem",
    whiteSpace: "nowrap",
  },
  // Franja azul a todo el ancho de la tarjeta (los márgenes negativos anulan el relleno de la tarjeta).
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "0.75rem",
    margin: "-1.25rem -1.4rem 1rem",
    padding: "1rem 1.4rem",
    background: TEMA.franja,
    color: TEMA.franjaTexto,
    borderBottom: `4px solid ${TEMA.dorado}`,
    borderRadius: "13px 13px 0 0",
  },
  iconoSolicitud: {
    width: 42,
    height: 42,
    borderRadius: 10,
    background: "var(--tema-suave, #dceaf5)",
    color: "var(--tema-primario, #2f6690)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1.25rem",
    flexShrink: 0,
  },
  tituloTabla: {
    ...tituloSeccionTema,
    fontSize: "0.86rem",
    margin: "1.1rem 0 0.6rem",
  },
  subtexto: { color: TEMA.franjaSub, fontSize: "0.85rem", marginTop: "0.15rem" },
  totalBox: {
    background: "#dcf0e6",
    borderRadius: 8,
    padding: "0.5rem 0.9rem",
    textAlign: "right",
  },
  totalLabel: { fontSize: "0.68rem", color: "#2f7a54", fontWeight: 700, letterSpacing: "0.03em" },
  totalValor: { fontSize: "1.25rem", fontWeight: 700, color: "#2f7a54" },
  linkVolver: { color: "var(--tema-titulo, #0b3c60)", fontWeight: 600, fontSize: "0.88rem", textDecoration: "none" },
  // Mismo diseño que "Datos de la solicitud" en el detalle: línea de color, título y fondo suave.
  datosCard: {
    marginTop: "0.9rem",
    marginBottom: "0.9rem",
    border: "1px solid #e2e6ea",
    borderTop: "3px solid var(--tema-primario, #1f4e8c)",
    borderRadius: 12,
    padding: "0.8rem 1rem 1rem",
    background: "#fff",
  },
  datosEncabezado: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.5rem", flexWrap: "wrap", marginBottom: "0.7rem" },
  datosPanel: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
    gap: "0.8rem 1.4rem",
    fontSize: "0.88rem",
    fontWeight: 600,
    color: "var(--tema-titulo, #0b3c60)",
    background: "var(--tema-suave, #f5f8fc)",
    border: "1px solid var(--tema-linea, #e2e8f0)",
    borderRadius: 10,
    padding: "0.8rem 1rem",
  },
  datosRequerimiento: { gridColumn: "1 / -1", paddingTop: "0.7rem", borderTop: "1px dashed var(--tema-linea, #d5deec)" },
  cajaRecomendacion: {
    display: "flex",
    alignItems: "center",
    gap: "0.7rem",
    background: "#e8f6ee",
    border: "1px solid #bfe2cd",
    borderLeft: "5px solid #2f7a54",
    borderRadius: 10,
    padding: "0.65rem 0.9rem",
    margin: "0.4rem 0 0.2rem",
    color: "#2f7a54",
  },
  // Cuadro comparativo: el encabezado, el pie (totales y botones) y las primeras columnas quedan fijos al desplazarse.
  comparadorScroll: {
    overflow: "auto",
    maxHeight: "70vh",
    border: "1px solid #dfe4ea",
    borderRadius: 10,
  },
  comparador: { width: "100%", borderCollapse: "separate", borderSpacing: 0, fontSize: "0.84rem" },
  thComparador: {
    position: "sticky",
    top: 0,
    zIndex: 2,
    background: "var(--tema-primario, #1f4e8c)",
    color: "#fff",
    fontSize: "0.74rem",
    fontWeight: 700,
    padding: "0.55rem 0.6rem",
    textAlign: "center",
    verticalAlign: "top",
    borderBottom: "3px solid var(--tema-acento, #fec900)",
    whiteSpace: "nowrap",
  },
  thRecomendada: { background: "#2f7a54" },
  colFijaComparador: { position: "sticky", left: 0, zIndex: 1 },
  tdComparador: { padding: "0.5rem 0.6rem", borderBottom: "1px solid #eef1f3", verticalAlign: "middle" },
  etiquetaRecomendada: { fontSize: "0.66rem", fontWeight: 700, color: "#fff3c4", marginTop: "0.2rem" },
  linkCotizacion: { display: "inline-block", marginTop: "0.3rem", fontSize: "0.7rem", fontWeight: 600, color: "#fff", textDecoration: "underline" },
  etiquetaMasBajo: {
    display: "inline-block",
    marginTop: "0.2rem",
    fontSize: "0.66rem",
    fontWeight: 700,
    color: "#1f7a4a",
    background: "#d6f0e1",
    borderRadius: 999,
    padding: "0.05rem 0.45rem",
  },
  tdPie: {
    background: "#eef3fa",
    fontWeight: 800,
    color: "var(--tema-titulo, #0b3c60)",
    padding: "0.55rem 0.6rem",
    textAlign: "center",
    borderTop: "2px solid var(--tema-primario, #1f4e8c)",
    fontVariantNumeric: "tabular-nums",
  },
  tdPieSecundario: { background: "#f7f9fc", padding: "0.5rem 0.6rem", textAlign: "center", borderTop: "1px solid #e2e6ea", fontSize: "0.8rem", color: "#3d4a5c" },
  tdPieRecomendada: { background: "#e3f3ea" },
  avisoNoMasBarato: {
    display: "inline-block",
    marginLeft: "0.4rem",
    fontWeight: 700,
    color: "#8a5a00",
    background: "#fdf3d7",
    border: "1px solid #efd98f",
    borderRadius: 999,
    padding: "0.05rem 0.5rem",
  },
  tarjetaProveedor: {
    border: "1px solid #dfe4ea",
    borderRadius: 12,
    padding: "0.75rem 0.85rem",
    marginBottom: "0.75rem",
    background: "#fff",
    boxShadow: "0 1px 3px rgba(27,42,74,0.06)",
  },
  tarjetaProveedorRecomendada: { border: "2px solid #2f7a54", background: "#f3fbf6" },
  tarjetaProveedorEncabezado: { display: "flex", alignItems: "center", gap: "0.55rem", paddingBottom: "0.55rem", borderBottom: "1px solid #eef1f3" },
  tarjetaProveedorDatos: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "0.4rem 0.8rem",
    fontSize: "0.78rem",
    color: "#3d4a5c",
    padding: "0.5rem 0",
    borderBottom: "1px solid #eef1f3",
  },
  tarjetaProveedorLinea: { display: "flex", alignItems: "center", gap: "0.6rem", padding: "0.45rem 0", borderBottom: "1px dashed #e6eaf0" },
  etiquetaDato: { display: "block", fontSize: "0.68rem", fontWeight: 700, color: "#8993a1", textTransform: "uppercase", letterSpacing: "0.02em", marginBottom: "0.1rem" },
  detalleProducto: { fontSize: "0.76rem", color: "#5b6472", fontWeight: 400 },
  botonAprobarFila: {
    background: "#2f7a54",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    padding: "0.3rem 0.6rem",
    fontSize: "0.74rem",
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  botonRechazarFila: {
    background: "#fff",
    color: "#a3402f",
    border: "1px solid #a3402f",
    borderRadius: 6,
    padding: "0.3rem 0.6rem",
    fontSize: "0.74rem",
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  alertaRoja: {
    background: "#f7ded9",
    borderLeft: "3px solid #a3402f",
    borderRadius: "0 8px 8px 0",
    padding: "0.6rem 0.9rem",
    fontSize: "0.82rem",
    color: "#a3402f",
    marginBottom: "0.75rem",
  },
  tabla: { width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" },
  listaArticulos: {
    margin: 0,
    paddingLeft: "1.1rem",
  },
  etiquetaYaAprobado: {
    color: "#2f7a54",
    textDecoration: "none",
    fontWeight: 600,
    fontSize: "0.78rem",
  },
  th: {
    padding: "0.5rem 0.4rem",
    border: "1px solid #d3d9df",
    fontSize: "0.8rem",
    ...thTema,
  },
  td: { padding: "0.4rem" },
  avatar: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 24,
    height: 24,
    borderRadius: "50%",
    fontSize: "0.75rem",
    fontWeight: 700,
    color: "#fff",
    flexShrink: 0,
  },
  avatarVerde: { background: "#2f7a54" },
  avatarGris: { background: "#8993a1" },
  badgeRecomendada: {
    fontSize: "0.68rem",
    fontWeight: 700,
    color: "#2f7a54",
    background: "#dcf0e6",
    borderRadius: 999,
    padding: "0.1rem 0.5rem",
    whiteSpace: "nowrap",
  },
  botonVerCotizacion: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.3rem",
    fontSize: "0.78rem",
    fontWeight: 600,
    color: "var(--tema-primario, #2f6690)",
    border: "1px solid #b8d2e8",
    borderRadius: 999,
    padding: "0.3rem 0.75rem",
    background: "#fff",
    textDecoration: "none",
    whiteSpace: "nowrap",
  },
  cantidad: { color: "#5b6472", fontSize: "0.8rem" },
  boton: {
    border: "none",
    borderRadius: 10,
    padding: "0.55rem 1.2rem",
    fontSize: "0.85rem",
    fontWeight: 700,
    color: "#fff",
    cursor: "pointer",
  },
  botonAprobar: { background: "#2f7a54" },
  botonRechazar: { background: "#a3402f" },
  botonVer: {
    display: "inline-block",
    border: "1px solid var(--tema-primario, #2f6690)",
    borderRadius: 10,
    padding: "0.5rem 1.1rem",
    fontSize: "0.85rem",
    fontWeight: 600,
    color: "var(--tema-primario, #2f6690)",
    textDecoration: "none",
  },
};

export default DetalleAprobacion;
